import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfigHolder from "../../config/ConfigHolder";
import useWidgetStore from "../../store/useWidgetStore";
import { useSettings } from "../../context/SettingsContext";
/**
 * The waiting lists the signed in consumer is on with this company, and signing off one of them.
 *
 * This is the consumer's own waiting list overview (/consumer/waitinglist/list) in the widget:
 * what each list is waiting for, when they signed up, when they were last told about a place,
 * and whether one is free right now.
 *
 * One endpoint serves both: POST asks for the list, DELETE signs the consumer off one waiting
 * list. Either way the answer is the list as it is afterwards, so the response of a delete is
 * written straight into the query - there is nothing left to refetch.
 *
 * The consumer is the one in the store, sent as the token the sign in returned. Without a signed
 * in consumer there is nothing to ask for, so the query does not run.
 */
export const useConsumerWaitinglists = (options) => {
    const { api_key, widget_id } = useSettings();
    const { consumer } = useWidgetStore();
    const queryClient = useQueryClient();
    const queryKey = ['widget-consumer-waitinglists', consumer === null || consumer === void 0 ? void 0 : consumer.id];
    const url = () => ConfigHolder.getApiMethodUrl(api_key, widget_id, 'waitinglists', 'consumer');
    const headers = {
        'Content-Type': 'application/json', // checks this header if it's a POST, while calling the method getRestData.... That's not Restful
        'X-OAnl-csrf-protection': 'on',
        'accept': 'application/json+ld',
    };
    const defaultOptions = Object.assign(Object.assign({}, options), { enabled: !!(consumer === null || consumer === void 0 ? void 0 : consumer.id), networkMode: "always", cacheTime: 0, staleTime: 0 });
    const query = useQuery(Object.assign(Object.assign({}, defaultOptions), { queryKey: queryKey, queryFn: async ({ signal }) => {
            var _a;
            let data = {
                consumerId: (_a = consumer === null || consumer === void 0 ? void 0 : consumer.id) !== null && _a !== void 0 ? _a : ''
            };
            const response = await fetch(url().href, {
                signal: signal,
                method: "POST",
                headers: headers,
                body: JSON.stringify(data)
            });
            return response.json();
        } }));
    const remove = useMutation({
        mutationFn: async (id) => {
            var _a;
            let data = {
                consumerId: (_a = consumer === null || consumer === void 0 ? void 0 : consumer.id) !== null && _a !== void 0 ? _a : '',
                id: id
            };
            const response = await fetch(url().href, {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify(data)
            });
            return response.json();
        },
        onSuccess: (result) => {
            var _a;
            // the delete answers with the list the consumer is left with, so what is on screen
            // is already in hand: no second request to show it
            if ((_a = result === null || result === void 0 ? void 0 : result.result) === null || _a === void 0 ? void 0 : _a.item) {
                queryClient.setQueryData(queryKey, result);
            }
        }
    });
    return {
        /** The list itself, to render from. */
        query,
        /** Sign the consumer off one waiting list, by id. */
        remove
    };
};
